import React, { useContext, useState, useEffect } from "react";
import useReviews from "./useReviews";
import CFInput from "../shared/forms/CFInput";
import CFSelect from "../shared/forms/CFSelect";
import CFImageUpload from "../shared/forms/CFImageUpload";
import { VariantContextData } from "../variants/VariantBuilderContext";
import { createPortal } from "react-dom";

const ReviewSettings = (props) => {
  const review = props.review;
  const element = props.element;
  const review_recommendation = props.review_recommendation;
  const website = props.website;
  const [contextData, builder] = useContext(VariantContextData);
  const { Reviews } = useReviews();

  const [pendingChanges, setPendingChanges] = useState({});

  const includeChange = (payload) => {
    setPendingChanges((prev) => ({ ...prev, ...payload }));
  }

  const submitPendingChanges = _.debounce(() => {
    Reviews.update({ ...review, ...pendingChanges }, () => {
      // Update a timestamp to trigger refresh of reviews across the builder
      const targetObject = review_recommendation || element;
      if (targetObject) {
        builder.update([
          {
            object_type: review_recommendation ? "review_recommendations" : "elements",
            object_id: targetObject.id,
            setting_name: "[options][reviews-last-updated]",
            value: Date.now().toString()
          }
        ]);
      }

      setPendingChanges([]);
    });
  }, 200);

  const handleReviewSettings = function (event) {
    const { review_id } = event.detail;

    if (review_id.toString() == review.id.toString()) {
      setPanelVisibility(true);
    }
  };

  useEffect(() => {
    document.addEventListener("openReviewSettings", handleReviewSettings);

    return function cleanup() {
      document.removeEventListener("openReviewSettings", handleReviewSettings);
    };
  }, []);

  const [panelVisibility, setPanelVisibility] = useState(false);

  if (!panelVisibility) {
    return null;
  }

  return createPortal(
    <div
      className="modal"
      role="dialog"
      aria-labelledby="myModalLabel"
      data-backdrop="false"
      style={{
        display: "block",
        background: "rgb(0, 0, 0, 0.5)",
        overflowY: "auto",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget || !e.target.closest('.modal-content')) {
          if (e.target.closest('[class*="menu"]')) return;
          setPanelVisibility(false);
          submitPendingChanges();
        }
      }}
    >
      <div
        className="modal-dialog !w-[100%] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content hard-center !w-[690px] bg-white dark:bg-slate-950 !shadow-2xl">
          <div className="modal-header !border-slate-200 dark:!border-slate-800">
            <button
              type="button"
              className="close dark:text-white"
              onClick={() => {
                setPanelVisibility(false);
                submitPendingChanges();
              }}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <h4 className="text-2xl font-bold text-black dark:text-white whitespace-nowrap text-ellipsis overflow-hidden">
              Setting
            </h4>
          </div>
          <div className="modal-body">
            <div className="row hard-center">
              <div className="mx-[30px]">
                <div className="mb-[5px] no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Rating</span>
                </div>

                <CFInput
                  name="rating"
                  placeholder="5"
                  minLength={0}
                  maxLength={5}
                  value={review.rating}
                  onBlur={(value) => {
                    includeChange({ rating: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Title</span>
                </div>

                <CFInput
                  name="title"
                  placeholder="Best product ever"
                  value={review.title}
                  onBlur={(value) => {
                    includeChange({ title: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Content</span>
                </div>

                <CFInput
                  name="content"
                  placeholder="Best product ever"
                  value={review.content}
                  onBlur={(value) => {
                    includeChange({ content: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Customer Name</span>
                </div>

                <CFInput
                  name="customer_name"
                  value={review.customer_name}
                  placeholder="John Doe"
                  onBlur={(value) => {
                    includeChange({ customer_name: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Customer Title</span>
                </div>

                <CFInput
                  name="customer_title"
                  value={review.customer_title}
                  placeholder="CEO"
                  onBlur={(value) => {
                    includeChange({ customer_title: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Customer Avatar</span>
                </div>

                <CFImageUpload
                  name="customer_avatar"
                  className="mt-5"
                  showInput={true}
                  value={review.customer_avatar || ""}
                  websiteId={website.id}
                  environment={contextData.environment}
                  uploadPath={`/reviews/${review.id}/customer_avatars/`}
                  previewMaxWidth="100px"
                  onBlur={(value) => {
                    includeChange({ customer_avatar: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Brand Logo</span>
                </div>

                <CFImageUpload
                  name="brand_logo"
                  className="mt-5"
                  showInput={true}
                  value={review.brand_logo || ""}
                  websiteId={website.id}
                  environment={contextData.environment}
                  uploadPath={`/reviews/${review.id}/brand_logos/`}
                  previewMaxWidth="100px"
                  onBlur={(value) => {
                    includeChange({ brand_logo: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Video</span>
                </div>

                <CFInput
                  name="video"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={review.video}
                  onBlur={(value) => {
                    includeChange({ video: value });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Video Positioning</span>
                </div>

                <CFSelect
                  name="video_positioning"
                  className="smart-select"
                  value={(() => {
                    const effectiveOptions = pendingChanges.options || review.options;
                    const positioning = effectiveOptions["video-positioning"];
                    if (!positioning) return null;
                    const labels = { background: "Background", left: "Left", right: "Right", inline: "In Content" };
                    return { value: positioning, label: labels[positioning] || positioning };
                  })()}
                  placeholder="Background"
                  options={[
                    { value: "background", label: "Background" },
                    { value: "left", label: "Left" },
                    { value: "right", label: "Right" },
                    { value: "inline", label: "In Content" }
                  ]}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                  onChange={(selectedOption) => {
                    const value = selectedOption ? selectedOption.value : null;
                    includeChange({ options: { ...review.options, "video-positioning": value } });
                  }}
                />

                <div className="mb-[5px] mt-10 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Source</span>
                </div>

                <CFInput
                  wysiwyg={true}
                  name="source"
                  website={website}
                  placeholder="ConvertFlow"
                  value={review.options["source"]}
                  onBlur={(value) => includeChange({ options: { ...review.options, "source": value } })}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Source Link</span>
                </div>

                <CFInput
                  name="source_link"
                  placeholder="https://www.convertflow.com"
                  value={review.options["source_link"]}
                  onBlur={(value) => {
                    includeChange({ options: { ...review.options, "source_link": value } });
                  }}
                />

                <div className="mb-[5px] mt-5 no-padding-sides">
                  <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">Date</span>
                </div>

                <CFInput
                  type="date"
                  name="date"
                  placeholder="2024-06-27"
                  value={review.date}
                  onBlur={(value) => {
                    includeChange({ date: value });
                  }}
                />
              </div>
              <div className="col-xs-12 vertical-space">
                <div className="col-xs-12">
                  <button
                    type="button"
                    className="btn blue-button float-left"
                    onClick={() => {
                      submitPendingChanges();
                      setPanelVisibility(false);
                    }}
                  >
                    Save & Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReviewSettings;
